import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import Typography from "@mui/material/Typography";
import { keyframes } from "@mui/system";
import { GoalAchievedSummary } from "../../../utils/goal/goalCompletion";

type GoalAchievedDialogViewProps = {
  goal: GoalAchievedSummary | null;
  startedOn: string;
  onWriteAbout: () => void;
  onClose: () => void;
};

const rise = keyframes`
  0% { opacity: 0; transform: translateY(40px) scale(0.92); }
  100% { opacity: 1; transform: none; }
`;
const badgePop = keyframes`
  0% { transform: scale(0) rotate(-30deg); }
  100% { transform: scale(1) rotate(0deg); }
`;
const wave = keyframes`
  0% { transform: scale(1); opacity: 0.7; }
  100% { transform: scale(1.9); opacity: 0; }
`;
const drawTick = keyframes`
  to { stroke-dashoffset: 0; }
`;
const fadeUp = keyframes`
  0% { opacity: 0; transform: translateY(10px); }
  100% { opacity: 1; transform: none; }
`;

const reducedMotion = {
  "@media (prefers-reduced-motion: reduce)": {
    animationDuration: "1ms",
    animationDelay: "0ms",
  },
};

const stagger = (delay: number) => ({
  animation: `${fadeUp} 420ms ${delay}ms ease-out both`,
  ...reducedMotion,
});

const waveSx = (delay: number) => ({
  position: "absolute",
  inset: 0,
  borderRadius: "50%",
  border: 2,
  borderColor: "primary.main",
  animation: `${wave} 1300ms ${delay}ms ease-out both`,
  "@media (prefers-reduced-motion: reduce)": { display: "none" },
});

const Stat: React.FC<{ value: React.ReactNode; label: string }> = ({
  value,
  label,
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 0.25,
      flex: 1,
      minWidth: 0,
    }}
  >
    {value}
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

const GoalAchievedDialogView: React.FC<GoalAchievedDialogViewProps> = ({
  goal,
  startedOn,
  onWriteAbout,
  onClose,
}) => {
  const stats = goal
    ? [
        goal.subGoalsTotal > 0 && (
          <Stat
            key="subgoals"
            value={
              <Typography variant="h6">
                {goal.subGoalsDone} of {goal.subGoalsTotal}
              </Typography>
            }
            label="sub-goals"
          />
        ),
        goal.themes.length > 0 && (
          <Stat
            key="theme"
            value={
              <Chip
                label={goal.themes[0]}
                size="small"
                color="primary"
                sx={{ marginY: 0.5, maxWidth: "100%" }}
              />
            }
            label={goal.themes.length > 1 ? `+${goal.themes.length - 1} more` : "theme"}
          />
        ),
      ].filter(Boolean)
    : [];

  return (
    <Dialog
      open={goal !== null}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        // The badge says "achieved" visually; say it to screen readers too
        "aria-label": goal ? `Goal achieved: ${goal.title}` : undefined,
        sx: {
          margin: 2.5,
          width: "calc(100% - 40px)",
          animation: `${rise} 520ms 80ms cubic-bezier(.34,1.4,.64,1) both`,
          ...reducedMotion,
        },
      }}
    >
      {goal && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            textAlign: "center",
            paddingX: 3,
            paddingTop: 4,
            paddingBottom: 2.5,
          }}
        >
          <Box sx={{ position: "relative", width: 88, height: 88, marginBottom: 0.5 }}>
            <Box aria-hidden sx={waveSx(420)} />
            <Box aria-hidden sx={waveSx(640)} />
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                borderRadius: "50%",
                backgroundColor: "primary.main",
                display: "grid",
                placeItems: "center",
                boxShadow: "0 8px 20px rgba(32,167,111,.4)",
                animation: `${badgePop} 560ms 260ms cubic-bezier(.34,1.7,.64,1) both`,
                ...reducedMotion,
              }}
            >
              <Box
                component="svg"
                viewBox="0 0 24 24"
                aria-hidden
                sx={{
                  width: 48,
                  height: 48,
                  "& path": {
                    fill: "none",
                    stroke: "#fff",
                    strokeWidth: 3.2,
                    strokeLinecap: "round",
                    strokeLinejoin: "round",
                    strokeDasharray: 24,
                    strokeDashoffset: 24,
                    animation: `${drawTick} 380ms 560ms ease-out both`,
                    ...reducedMotion,
                  },
                }}
              >
                <path d="M6 12.6l4 4 8-8.6" />
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, ...stagger(420) }}>
            <Typography variant="h5" component="h2">
              {goal.title}
            </Typography>
            {startedOn && (
              <Typography variant="body2" color="text.secondary">
                You started on {startedOn}.{" "}
                {goal.days === 0 ? "Done the same day." : `${goal.days} ${goal.days === 1 ? "day" : "days"} later, it's done.`}
              </Typography>
            )}
            {Boolean(goal.alsoCompleted) && (
              <Typography variant="body2" color="text.secondary">
                You also completed {goal.alsoCompleted} other{" "}
                {goal.alsoCompleted === 1 ? "goal" : "goals"} this week.
              </Typography>
            )}
          </Box>
          {stats.length > 0 && (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                gap: 1,
                paddingY: 1.75,
                borderTop: 1,
                borderBottom: 1,
                borderColor: "divider",
                ...stagger(560),
              }}
            >
              {stats}
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              ...stagger(700),
            }}
          >
            <Button variant="contained" onClick={onWriteAbout}>
              Write in journal
            </Button>
            <Button onClick={onClose}>Done</Button>
          </Box>
        </Box>
      )}
    </Dialog>
  );
};

export default GoalAchievedDialogView;
